<template>
  <div class="search-page">
    <div class="container">
      <div class="search-header">
        <h1>搜索结果: "{{ searchKeyword }}"</h1>
        <div class="search-form">
          <input 
            type="text" 
            placeholder="搜索漫画..." 
            v-model="searchKeyword"
            @keyup.enter="searchComic"
          >
          <button @click="searchComic">搜索</button>
        </div>
      </div>
      
      <div class="loading" v-if="isLoading">
        <div class="spinner"></div>
        <p>搜索中...</p>
      </div>
      
      <div class="no-results" v-else-if="!isLoading && comics.length === 0">
        <div class="no-results-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            <line x1="8" y1="11" x2="14" y2="11"></line>
          </svg>
        </div>
        <h3>未找到相关结果</h3>
        <p>没有找到与"{{ searchKeyword }}"相关的漫画</p>
        <p>您可以尝试其他关键词或浏览我们的<router-link to="/category">漫画分类</router-link></p>
      </div>
      
      <div class="search-results" v-else>
        <div class="total-count">找到 {{ total }} 个结果</div>
        <div class="comics-grid">
          <div class="comic-item" v-for="comic in comics" :key="comic.id" @click="goToComicDetail(comic.id)">
            <div class="comic-cover">
              <img :src="getFullImageUrl(comic.cover)" :alt="comic.title">
              <div class="comic-badge" v-if="comic.is_paid">VIP</div>
            </div>
            <div class="comic-info">
              <h3 class="comic-title">{{ comic.title }}</h3>
              <p class="comic-author">{{ comic.author }}</p>
              <p class="comic-desc">{{ truncateDesc(comic.description) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../api/index'
import request from '../api/request'
import { useRouter, useRoute } from 'vue-router'
import { ref, onMounted, watch } from 'vue'

export default {
  name: 'SearchPage',
  setup() {
    const router = useRouter()
    const route = useRoute()
    
    const searchKeyword = ref('')
    const comics = ref([])
    const total = ref(0)
    const isLoading = ref(false)
    
    const getFullImageUrl = (cover) => {
      if (!cover) return 'https://via.placeholder.com/180x240'
      if (/^https?:\/\//i.test(cover)) return cover
      if (cover.startsWith('//')) return window.location.protocol + cover
      return request.baseUrl + (cover.startsWith('/') ? cover : `/${cover}`)
    }
    
    const truncateDesc = (desc) => {
      if (!desc) return '暂无描述'
      return desc.length > 60 ? desc.substring(0, 60) + '...' : desc
    }
    
    const searchComic = async () => {
      if (!searchKeyword.value || searchKeyword.value.trim() === '') return
      
      isLoading.value = true
      comics.value = []
      total.value = 0
      
      try {
        const result = await api.searchComic(searchKeyword.value.trim())
        
        if (result && result.code === 1 && result.data) {
          // 青少年模式的内容过滤已由服务端完成
          comics.value = result.data.list || []
          total.value = comics.value.length
          
          // 更新URL，不触发页面重载
          const query = { ...route.query, keyword: searchKeyword.value }
          router.replace({ path: route.path, query })
        }
      } catch (error) {
        console.error('搜索漫画失败:', error)
      } finally {
        isLoading.value = false
      }
    }
    
    const goToComicDetail = (id) => {
      router.push(`/comic/${id}`)
    }
    
    // 监听路由参数变化
    watch(() => route.query.keyword, (newKeyword) => {
      if (newKeyword) {
        searchKeyword.value = newKeyword
        searchComic()
      }
    }, { immediate: true })
    
    onMounted(() => {
      // 初始化搜索关键词
      if (route.query.keyword) {
        searchKeyword.value = route.query.keyword
        searchComic()
      }
    })
    
    return {
      searchKeyword,
      comics,
      total,
      isLoading,
      getFullImageUrl,
      truncateDesc,
      searchComic,
      goToComicDetail
    }
  }
}
</script>

<style scoped>
.search-page {
  padding: 30px 0;
  min-height: calc(100vh - 70px);
  background-color: #f5f5f5;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.search-header {
  margin-bottom: 30px;
}

.search-header h1 {
  font-size: 24px;
  margin-bottom: 15px;
  color: #333;
}

.search-form {
  display: flex;
  max-width: 600px;
}

.search-form input {
  flex: 1;
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 4px 0 0 4px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.3s;
}

.search-form input:focus {
  border-color: #ff6b6b;
}

.search-form button {
  background-color: #ff6b6b;
  color: #fff;
  border: none;
  padding: 0 20px;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  transition: background-color 0.3s;
}

.search-form button:hover {
  background-color: #ff5252;
}

.loading {
  text-align: center;
  padding: 50px 0;
}

.spinner {
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 107, 107, 0.3);
  border-radius: 50%;
  border-top-color: #ff6b6b;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.no-results {
  text-align: center;
  padding: 60px 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.no-results-icon {
  color: #ccc;
  margin-bottom: 20px;
}

.no-results h3 {
  font-size: 20px;
  margin-bottom: 10px;
}

.no-results p {
  color: #666;
  margin-bottom: 10px;
}

.no-results a {
  color: #ff6b6b;
  text-decoration: none;
}

.total-count {
  margin-bottom: 20px;
  color: #666;
  font-size: 14px;
}

.comics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
}

.comic-item {
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;
  cursor: pointer;
}

.comic-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.comic-cover {
  position: relative;
  height: 240px;
}

.comic-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.comic-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: #ff6b6b;
  color: #fff;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 3px;
}

.comic-info {
  padding: 15px;
}

.comic-title {
  font-size: 16px;
  margin: 0 0 5px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.comic-author {
  font-size: 14px;
  color: #666;
  margin: 0 0 10px;
}

.comic-desc {
  font-size: 13px;
  color: #999;
  margin: 0;
  line-height: 1.5;
  height: 60px;
  overflow: hidden;
}

@media (max-width: 768px) {
  .comics-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }
  
  .comic-cover {
    height: 200px;
  }
  
  .comic-info {
    padding: 10px;
  }
  
  .comic-desc {
    display: none;
  }
}
</style> 
